import { z } from "zod";
import type { ConfigTypeId } from "../../registry";
import { defineStepConfig } from "../../registry";

/**
 * Stable type identifier for the external HTTP call step (orchestration, tests).
 */
export const EXTERNAL_HTTP_CALL_STEP_TYPE =
  "external_http_call" satisfies ConfigTypeId;

/**
 * External HTTP Call Step
 *
 * Generic outbound HTTP step. Runs in one of two modes:
 *
 * 1. **Registered mode** — picks a row from the org-scoped
 *    `core_external_api_definitions` registry. The node's input sockets are
 *    derived from `paramSchema`. Output sockets come from an optional pinned
 *    **response body** schema (root fields → typed sockets) or, when unset,
 *    from manual `responseSchema` JSONPath mappings (plus reserved outputs).
 *    Behavior matches `interpolate_email_template` (see
 *    `instantiateTemplate.ts`).
 *
 * 2. **Inline mode** — one-off URL/method/body configured on the step
 *    itself. Generic `body` (record) input; outputs are reserved sockets plus
 *    optional typed sockets from a pinned response-body schema (same root-field
 *    rules as registered mode).
 *
 * The handler is hardened: SSRF-safe DNS resolution, body-size caps,
 * header sanitization, automatic Idempotency-Key for non-GET. Registered APIs
 * store credentials encrypted; inline mode does not persist auth secrets (use
 * the API registry for authenticated calls). See
 * `backend/src/workflows/steps/integrations/external-http-call/README.md`.
 */

/**
 * HTTP methods supported by both registered and inline modes.
 *
 * Methods limited to those an HTTP client typically uses for REST APIs;
 * we deliberately omit OPTIONS / HEAD to keep the surface small.
 */
export const EXTERNAL_HTTP_METHODS = [
  "GET",
  "POST",
  "PUT",
  "PATCH",
  "DELETE",
] as const;
export type ExternalHttpMethod = (typeof EXTERNAL_HTTP_METHODS)[number];

/**
 * Parameter source — matches the OpenAPI / Postman convention so the cURL
 * import path can map directly without a translation table.
 */
export const EXTERNAL_API_PARAM_LOCATIONS = [
  "path",
  "query",
  "header",
  "body",
] as const;
export type ExternalApiParamLocation =
  (typeof EXTERNAL_API_PARAM_LOCATIONS)[number];

/**
 * Param value types — these map to socket types so the workflow editor can
 * draw the right handles. Keep this list minimal and stable; if a caller
 * needs a richer shape they should pick `object` and validate downstream.
 */
export const EXTERNAL_API_PARAM_TYPES = [
  "string",
  "number",
  "boolean",
  "object",
  "array",
] as const;
export type ExternalApiParamType = (typeof EXTERNAL_API_PARAM_TYPES)[number];

/**
 * Output socket types — `string`/`number`/`boolean` map directly to those
 * socket types; `json` covers everything else (objects, arrays). Keeping
 * the list small avoids forcing every caller to reason about every Zod
 * type variant.
 */
export const EXTERNAL_API_RESPONSE_TYPES = [
  "string",
  "number",
  "boolean",
  "json",
] as const;
export type ExternalApiResponseType =
  (typeof EXTERNAL_API_RESPONSE_TYPES)[number];

/**
 * Description of one declared parameter on a registered API.
 *
 * Mirrors the field-shape `httpTriggerFieldSchema` already uses
 * elsewhere (proven, JSON-schema-ish). `name` is also the socket id for
 * the corresponding input socket on the node.
 */
export const externalApiParamSchema = z.object({
  name: z
    .string()
    .min(1)
    // Socket ids must be valid identifiers so they can survive the workflow
    // editor's wiring; keep alphanum + underscore + dash.
    .regex(
      /^[a-zA-Z][a-zA-Z0-9_]*$/,
      "Param name must start with a letter and contain only letters, numbers, and underscores",
    ),
  in: z.enum(EXTERNAL_API_PARAM_LOCATIONS),
  type: z.enum(EXTERNAL_API_PARAM_TYPES),
  required: z.boolean().prefault(true),
  description: z.string().optional(),
  defaultValue: z.unknown().optional(),
});
export type ExternalApiParam = z.infer<typeof externalApiParamSchema>;

/**
 * One declared output mapping. `jsonPath` uses the dotted-path convention
 * (`order.lineItems.0.sku`) — keeps the response mapper trivial and
 * predictable. The runtime mapper resolves it against the JSON-decoded
 * response body.
 */
export const externalApiResponseFieldSchema = z.object({
  name: z
    .string()
    .min(1)
    .regex(
      /^[a-zA-Z][a-zA-Z0-9_]*$/,
      "Response field name must start with a letter and contain only letters, numbers, and underscores",
    ),
  jsonPath: z
    .string()
    .min(1)
    .describe(
      "Dotted path into the response JSON, e.g. 'order.lineItems.0.sku'",
    ),
  type: z.enum(EXTERNAL_API_RESPONSE_TYPES),
  description: z.string().optional(),
});
export type ExternalApiResponseField = z.infer<
  typeof externalApiResponseFieldSchema
>;

/**
 * Auth strategies. `authConfig` is opaque from the step's perspective; the
 * handler reads it and constructs the right header(s). All credential
 * material is encrypted at rest via `HybridEncryptionService.encryptBankingData`.
 */
export const EXTERNAL_API_AUTH_TYPES = [
  "none",
  "api_key_header",
  "bearer",
  "basic",
  "custom_headers",
] as const;
export type ExternalApiAuthType = (typeof EXTERNAL_API_AUTH_TYPES)[number];

export const externalApiAuthConfigSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("none") }),
  z.object({
    type: z.literal("api_key_header"),
    headerName: z.string().min(1),
    apiKey: z.string().min(1),
  }),
  z.object({
    type: z.literal("bearer"),
    token: z.string().min(1),
  }),
  z.object({
    type: z.literal("basic"),
    username: z.string().min(1),
    password: z.string().min(1),
  }),
  z.object({
    type: z.literal("custom_headers"),
    headers: z.record(z.string().min(1), z.string()),
  }),
]);
export type ExternalApiAuthConfig = z.infer<typeof externalApiAuthConfigSchema>;

export const externalApiRetryConfigSchema = z.object({
  maxAttempts: z.number().int().min(1).max(10).prefault(3),
  baseDelayMs: z.number().int().min(0).max(60_000).prefault(500),
  retryOnStatuses: z
    .array(z.number().int().min(100).max(599))
    .prefault([408, 429, 500, 502, 503, 504]),
  retryIdempotentOnly: z.boolean().prefault(true),
});
export type ExternalApiRetryConfig = z.infer<
  typeof externalApiRetryConfigSchema
>;

/**
 * Discriminated config for the step. Pick `registered` to point at a
 * row in the org's API registry; pick `inline` to make a one-off call
 * configured directly on this node.
 */
/**
 * Pins an org-wide extraction schema version whose `snapshot.fields` describe
 * the **JSON response body** contract — same catalog and pinning model as
 * `http_trigger`'s `bodySchemaDefinitionId` / `bodySchemaDefinitionVersionId`.
 *
 * When both are set on the step, they override the registry definition's pins
 * (registered mode). Omit both to fall back to registry defaults or manual
 * `responseSchema` mappings.
 */
const externalHttpCallResponseBodySchemaPinsSchema = z.object({
  responseBodySchemaDefinitionId: z
    .string()
    .uuid()
    .optional()
    .describe("Stored schema definition (same catalog as extraction schemas)."),
  responseBodySchemaDefinitionVersionId: z
    .string()
    .uuid()
    .optional()
    .describe(
      "Published schema version whose snapshot drives typed response output sockets.",
    ),
});

export const externalHttpCallConfigSchema = z
  .discriminatedUnion("mode", [
    z
      .object({
        mode: z.literal("registered"),
        apiDefinitionId: z.uuid(),
      })
      .extend(externalHttpCallResponseBodySchemaPinsSchema.shape),
    z
      .object({
        mode: z.literal("inline"),
        url: z.string().url(),
        method: z.enum(EXTERNAL_HTTP_METHODS),
        headers: z.record(z.string(), z.string()).optional(),
        auth: externalApiAuthConfigSchema.optional(),
        timeoutMs: z.number().int().min(100).max(600_000).optional(),
        retry: externalApiRetryConfigSchema.optional(),
      })
      .extend(externalHttpCallResponseBodySchemaPinsSchema.shape),
  ])
  .superRefine((data, ctx) => {
    const hasDef = data.responseBodySchemaDefinitionId !== undefined;
    const hasVer = data.responseBodySchemaDefinitionVersionId !== undefined;
    if (hasDef !== hasVer) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "Set both responseBodySchemaDefinitionId and responseBodySchemaDefinitionVersionId, or omit both.",
        path: hasDef
          ? ["responseBodySchemaDefinitionVersionId"]
          : ["responseBodySchemaDefinitionId"],
      });
    }
    if (
      data.mode === "inline" &&
      data.auth !== undefined &&
      data.auth.type !== "none"
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          'Inline mode cannot store API credentials on the workflow step. Register the endpoint under External APIs and use Registered mode, or set auth to "none".',
        path: ["auth"],
      });
    }
  });
export type ExternalHttpCallConfig = z.infer<
  typeof externalHttpCallConfigSchema
>;

/**
 * The step itself.
 *
 * Sockets are dynamic: the input/output schemas are intentionally generic
 * (`z.record(z.string(), z.unknown())`) so dynamic socket generation can
 * substitute the real shape at instantiation time. See
 * `backend/src/workflows/sockets/dynamicOutputSockets.ts` and
 * `backend/src/workflows/templates/instantiateTemplate.ts`.
 */
export const externalHttpCallStep = defineStepConfig({
  type: EXTERNAL_HTTP_CALL_STEP_TYPE,
  name: "External HTTP Call",
  description:
    "Calls an external HTTP API. Pick a registered API to inherit typed input/output sockets and store credentials encrypted, or use Inline mode for a one-off unauthenticated URL/method/body on this node.",
  category: "Integration",

  inputSchema: z.record(z.string(), z.unknown()),

  // Output sockets are dynamic; values are a flat map of socket id → value
  // (reserved outputs plus schema roots or manual responseSchema projections).
  outputSchema: z.record(z.string(), z.unknown()),

  configSchema: externalHttpCallConfigSchema,

  emits: [] as const,

  execution: {
    // Registered API timeouts can override; this is the outer cap that lets
    // Temporal reschedule on a wedged worker.
    startToCloseTimeout: "10 minutes",
    retry: {
      // Network-level retry happens inside the handler so it can respect
      // Retry-After and the per-API retry policy. Temporal-level retry is
      // limited to one attempt to avoid double-firing a non-idempotent POST.
      maximumAttempts: 1,
    },
    nonRetryableErrorTypes: ["ExternalHttpCallNonRetryableError"],
  },
});
